import { Placeholder } from '@sitecore-jss/sitecore-jss-nextjs';
import useHelpers from 'hooks/helpers';
import { useEffect, useMemo } from 'react';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import ScLink from 'components/Generic/SitecoreWrappers/ScLink';
import ArticleCard from 'components/Tiles/ArticleCard';
import { useLazyGetRecentArticlesQuery } from '@/store/articles/services/articleAPI';
import { ComponentProps } from 'lib/component-props';

type Props = ComponentProps & {
  fields: {
    baseUrl: {
      value: string;
    };
    contentGroup: {
      displayName: string;
      fields: {
        name: {
          value: string;
        };
      };
      id: string;
      name: string;
      url: string;
    };
    viewAll: {
      value: {
        anchor: string;
        class: string;
        href: string;
        id: string;
        linkType: string;
        querystring: string;
        target: string;
        text: string;
        title: string;
      };
    };
  };
};

const RecentArticles = ({ rendering, fields }: Props): JSX.Element => {
  const { anchorId } = useHelpers(rendering);
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor = sitecoreContext.pageEditing;
  const { sitecoreContext: context } = useSitecoreContext();

  const [fetchData, { data, isFetching }] = useLazyGetRecentArticlesQuery();

  const showViewAll =
    (!!fields?.viewAll?.value?.text && !!fields?.viewAll?.value?.href) || isExperienceEditor;
  const articlesExist = useMemo(() => {
    return data?.length !== 0;
  }, [data]);

  useEffect(() => {
    fetchData({
      siteId: context.siteRoot,
      language: context.language,
      contentGroupId: fields.contentGroup.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const articles = useMemo(() => {
    if (!articlesExist) return [];

    return data?.map((article) => {
      return {
        ...article,
        strapline: article.articleStrapline.value,
        articleType: article.template.id,
        publicationDate: article.articlePublicationDate.formattedDateValue,
        category: article.articleTags?.targetItems[0]?.name.value,
        baseUrl: fields.baseUrl.value,
        articleImage: article.articleImage?.src,
      };
    });
  }, [articlesExist, data, fields.baseUrl.value]);

  return (
    <section id={anchorId} className="default-section-padding background-white">
      <div className="container">
        <Placeholder name="abrdn-tiles-heading" rendering={rendering} className="mb-4" />
        {articlesExist && !isFetching && (
          <div className="row d-flex flex-wrap mb-2">
            {articles?.map((article) => (
              <div key={article.id} className="col-12 col-md-6 col-lg-3 mb-6">
                <ArticleCard fields={article} onRecentPage />
              </div>
            ))}
          </div>
        )}
        {showViewAll && (
          <ScLink
            field={fields.viewAll}
            className="revert-underline text-link background-to-change"
          />
        )}
      </div>
    </section>
  );
};

export default RecentArticles;
